import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Link,
  Redirect,
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom";
import { FaHome, FaSyncAlt, FaUserGraduate } from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import SelectSessionClass from "../ChildComponent/SelectSessionClass";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/StudentList.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function categorizeGender(row) {
  const g = row?.gender;
  if (g == null || String(g).trim() === "") return "unspecified";
  const s = String(g).trim().toLowerCase();
  if (["male", "m", "boy", "boys"].includes(s)) return "male";
  if (["female", "f", "girl", "girls"].includes(s)) return "female";
  return "unspecified";
}

//Created By Cursor
function admissionTypeKind(row) {
  const id = row?.admission_type_id;
  const s = id == null ? "" : String(id).trim();
  if (s === "ADMTYPE01") return "readm";
  if (s === "ADMTYPE02") return "new";
  return "other";
}

//Created By Cursor
function BoysGirlsInline({ boys, girls, unspec = 0, title }) {
  return (
    <div className="sl-stat-bg-inline" title={title}>
      <span className="sl-stat-bg-seg">
        <i
          className="fa fa-male sl-stat-bg-ico sl-stat-bg-ico--boys"
          aria-hidden="true"
        />
        <span className="sl-stat-adm-k sl-stat-adm-k--boys">BOYS:</span>
        <span className="sl-stat-adm-v sl-stat-adm-v--boys">{boys}</span>
      </span>
      <span className="sl-stat-bg-seg">
        <i
          className="fa fa-female sl-stat-bg-ico sl-stat-bg-ico--girls"
          aria-hidden="true"
        />
        <span className="sl-stat-adm-k sl-stat-adm-k--girls">GIRLS:</span>
        <span className="sl-stat-adm-v sl-stat-adm-v--girls">{girls}</span>
      </span>
      {unspec > 0 ? (
        <span className="sl-stat-bg-seg">
          <i
            className="fa fa-question sl-stat-bg-ico sl-stat-bg-ico--unspec"
            aria-hidden="true"
          />
          <span className="sl-stat-adm-k sl-stat-adm-k--unspec">N.S.:</span>
          <span className="sl-stat-adm-v sl-stat-adm-v--unspec">{unspec}</span>
        </span>
      ) : null}
    </div>
  );
}

//Created By Cursor
export default function StudentList() {
  const history = useHistory();
  const location = useLocation();
  const { class_id, section_id, session_id } = useParams();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedClassPosition, setSelectedClassPosition] = useState("0");
  const [sessionMeta, setSessionMeta] = useState(null);

  const loadStudents = useCallback(async (cancelToken) => {
    if (!class_id || !section_id || !session_id) return;
    setLoading(true);
    setStudents([]);
    try {
      const schoolCode = SessionManager.getSchoolCode();
      const params = {
        school_code: schoolCode,
        class_id,
        section_id,
        session_id,
      };
      const url = `${API_BASE()}getCurrentStudent.php`;
      console.log("Full URL: ", `${url}?${new URLSearchParams(params)}`);

      const response = await axios
        .get(url, cancelToken ? { params, cancelToken } : { params })
        .then((res) => res.data);

      if (response?.api_status === "OK") {
        setStudents(Array.isArray(response.data) ? response.data : []);
      } else {
        setStudents([]);
        if (response?.api_remark) {
          myToast.notify_warning(response.api_remark);
        }
      }
      setLoading(false);
    } catch (error) {
      if (axios.isCancel?.(error)) return;
      console.error(error);
      setStudents([]);
      myToast.notify_warning("Failed to load students.");
      setLoading(false);
    }
  }, [class_id, section_id, session_id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const cancelSource = axios.CancelToken.source();
    loadStudents(cancelSource.token);
    return () => {
      cancelSource.cancel("StudentList request cancelled");
    };
  }, [loadStudents]);

  useEffect(() => {
    const fromState = location?.state?.sessionMeta;
    if (fromState && String(fromState.session_id) === String(session_id)) {
      setSessionMeta(fromState);
      return;
    }
    const current = SessionManager.getCurrentSession?.();
    if (Array.isArray(current) && current[0]) {
      setSessionMeta(current[0]);
    }
  }, [location?.state, session_id]);

  const handleSelectSessionClass = (
    nextClassId,
    nextSectionId,
    nextSessionId,
    selected_position
  ) => {
    setSelectedClassPosition(String(selected_position ?? "0"));
    const sid = nextSessionId ?? sessionMeta?.session_id ?? session_id;
    if (
      nextClassId == null ||
      nextSectionId == null ||
      sid == null ||
      String(nextClassId) === "" ||
      String(nextSectionId) === ""
    ) {
      return;
    }
    if (
      String(nextClassId) !== String(class_id) ||
      String(nextSectionId) !== String(section_id) ||
      String(sid) !== String(session_id)
    ) {
      history.push({
        pathname: `/AdminMain/StudentList/${nextClassId}/${nextSectionId}/${sid}`,
        state: sessionMeta ? { sessionMeta } : location.state,
      });
    }
  };

  const studentListStats = useMemo(() => {
    let boys = 0;
    let girls = 0;
    let transportGranted = 0;
    let reAdmissionBoys = 0;
    let reAdmissionGirls = 0;
    let reAdmissionGenderUnspec = 0;
    let newAdmissionBoys = 0;
    let newAdmissionGirls = 0;
    let newAdmissionGenderUnspec = 0;
    for (const row of students) {
      const genderCat = categorizeGender(row);
      if (genderCat === "male") boys += 1;
      else if (genderCat === "female") girls += 1;

      if (row?.transport_granted === true || row?.transport_granted === 1) {
        transportGranted += 1;
      }

      const admStr =
        row?.admission_type_id == null
          ? ""
          : String(row.admission_type_id).trim();
      if (admStr === "ADMTYPE01") {
        if (genderCat === "male") reAdmissionBoys += 1;
        else if (genderCat === "female") reAdmissionGirls += 1;
        else reAdmissionGenderUnspec += 1;
      } else if (admStr === "ADMTYPE02") {
        if (genderCat === "male") newAdmissionBoys += 1;
        else if (genderCat === "female") newAdmissionGirls += 1;
        else newAdmissionGenderUnspec += 1;
      }
    }
    return {
      total: students.length,
      boys,
      girls,
      transportGranted,
      reAdmissionBoys,
      reAdmissionGirls,
      reAdmissionGenderUnspec,
      newAdmissionBoys,
      newAdmissionGirls,
      newAdmissionGenderUnspec,
    };
  }, [students]);

  const classLabel = useMemo(() => {
    const first = students[0];
    if (!first) return "Select a class";
    if (section_id === "No" || !first.section_name) {
      return first.class_name || "Class";
    }
    return `${first.class_name || "Class"} · ${first.section_name}`;
  }, [students, section_id]);

  const sessionLabel =
    sessionMeta?.session_name || sessionMeta?.session_id || session_id || "Session";

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <div
      className="content payroll-page sm-page stul-page"
      style={{ fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Students</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell sl-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaUserGraduate />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Student List</h1>
              <p className="sm-hero__sub">
                {classLabel} · {sessionLabel} · {studentListStats.total} student
                {studentListStats.total === 1 ? "" : "s"}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className={`pp-icon-btn${loading ? " spinning" : ""}`}
              onClick={() => loadStudents()}
              disabled={loading}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="sl-body">
          {sessionMeta ? (
            <div className="sl-filters">
              <SelectSessionClass
                action={handleSelectSessionClass}
                selected_position={selectedClassPosition}
                initial_class_id={class_id}
                initial_section_id={section_id}
                session_id={sessionMeta.session_id}
                session_name={sessionMeta.session_name}
                session_status={sessionMeta.status}
                start_date={sessionMeta.start_date}
                end_date={sessionMeta.end_date}
                sessionChangePermission={true}
              />
            </div>
          ) : null}

          <div
            className="sl-stats"
            aria-label="Students in this class: counts by gender and admission type"
          >
            <div className="sl-stats-grid">
              <div className="sl-stat sl-stat--total">
                <span className="sl-stat-label">Students</span>
                <span className="sl-stat-value">{studentListStats.total}</span>
              </div>
              <div className="sl-stat sl-stat--boys">
                <span className="sl-stat-label">
                  <i className="fa fa-male sl-stat-ico" aria-hidden="true" /> Boys
                </span>
                <span className="sl-stat-value">{studentListStats.boys}</span>
              </div>
              <div className="sl-stat sl-stat--girls">
                <span className="sl-stat-label">
                  <i className="fa fa-female sl-stat-ico" aria-hidden="true" />{" "}
                  Girls
                </span>
                <span className="sl-stat-value">{studentListStats.girls}</span>
              </div>
              <div className="sl-stat sl-stat--transport">
                <span className="sl-stat-label">
                  <i className="fa fa-bus sl-stat-ico" aria-hidden="true" />{" "}
                  Transport
                </span>
                <span className="sl-stat-value">
                  {studentListStats.transportGranted}
                </span>
              </div>
              <div className="sl-stat sl-stat--readm sl-stat--adm-type">
                <span className="sl-stat-label">
                  <i className="fa fa-repeat sl-stat-ico" aria-hidden="true" />{" "}
                  Re-admission
                </span>
                <BoysGirlsInline
                  boys={studentListStats.reAdmissionBoys}
                  girls={studentListStats.reAdmissionGirls}
                  unspec={studentListStats.reAdmissionGenderUnspec}
                  title={
                    studentListStats.reAdmissionGenderUnspec > 0
                      ? "N.S. = gender not specified"
                      : undefined
                  }
                />
              </div>
              <div className="sl-stat sl-stat--newadm sl-stat--adm-type">
                <span className="sl-stat-label">
                  <i
                    className="fa fa-certificate sl-stat-ico"
                    aria-hidden="true"
                  />{" "}
                  New
                </span>
                <BoysGirlsInline
                  boys={studentListStats.newAdmissionBoys}
                  girls={studentListStats.newAdmissionGirls}
                  unspec={studentListStats.newAdmissionGenderUnspec}
                  title={
                    studentListStats.newAdmissionGenderUnspec > 0
                      ? "N.S. = gender not specified"
                      : undefined
                  }
                />
              </div>
            </div>
          </div>

          {loading && !students.length ? (
            <div className="sl-loading">
              <MyLoader />
            </div>
          ) : null}

          {!loading && !students.length ? (
            <div className="sl-empty">No students found for this class.</div>
          ) : (
            <div className="sl-grid row">
              {students.map((item) => {
                const genderCat = categorizeGender(item);
                const admKind = admissionTypeKind(item);
                const imgSrc = item.student_img_path
                  ? String(item.student_img_path)
                  : "";
                return (
                  <div
                    key={`${item.student_enrollment_no}-${item.session_id}`}
                    className="col-12 col-sm-6 col-md-4 sl-grid__col"
                  >
                  <Link
                    className="sl-card-link"
                    to={`/AdminMain/StudentDetails/${item.student_enrollment_no}/${item.session_id}`}
                  >
                    <article className="sl-card">
                      <div className="sl-details">
                        <div className="sl-details-top">
                          <div className="sl-name-row">
                            <div className="sl-name">{item.stu_name}</div>
                            <div className="sl-symbol-row sl-symbol-row--with-name">
                              <span
                                className={`sl-sym sl-sym--g-${genderCat}`}
                                title={
                                  genderCat === "male"
                                    ? "Boy"
                                    : genderCat === "female"
                                      ? "Girl"
                                      : "Gender not specified"
                                }
                                aria-label={
                                  genderCat === "male"
                                    ? "Boy"
                                    : genderCat === "female"
                                      ? "Girl"
                                      : "Gender not specified"
                                }
                              >
                                {genderCat === "male" ? (
                                  <i className="fa fa-male" aria-hidden="true" />
                                ) : null}
                                {genderCat === "female" ? (
                                  <i
                                    className="fa fa-female"
                                    aria-hidden="true"
                                  />
                                ) : null}
                                {genderCat === "unspecified" ? (
                                  <i
                                    className="fa fa-question"
                                    aria-hidden="true"
                                  />
                                ) : null}
                              </span>
                              {admKind === "new" ? (
                                <span
                                  className="sl-sym sl-sym--adm sl-sym--adm-new"
                                  title="New admission"
                                  aria-label="New admission"
                                >
                                  <i
                                    className="fa fa-certificate"
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                              {admKind === "readm" ? (
                                <span
                                  className="sl-sym sl-sym--adm sl-sym--adm-readm"
                                  title="Re-admission"
                                  aria-label="Re-admission"
                                >
                                  <i
                                    className="fa fa-repeat"
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                              {item.transport_granted === true ||
                              item.transport_granted === 1 ? (
                                <span
                                  className="sl-sym sl-sym--transport"
                                  title="Transport granted"
                                  aria-label="Transport granted"
                                >
                                  <i className="fa fa-bus" aria-hidden="true" />
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div className="sl-father-inline">
                            <span className="sl-k">Father:</span>
                            <span className="sl-v">{item.father_name}</span>
                          </div>
                        </div>

                        <div className="sl-details-bottom">
                          <div className="sl-bottom-row">
                            <div className="sl-bottom-left">
                              <div className="sl-avatar">
                                {imgSrc ? (
                                  <img src={imgSrc} alt="" />
                                ) : (
                                  <div className="sl-avatar__placeholder" />
                                )}
                                <div className="sl-enroll">
                                  {item.student_enrollment_no}
                                </div>
                              </div>

                              <div className="sl-info-grid">
                                <div className="sl-info sl-info--inline">
                                  <span className="sl-k">SRN:</span>
                                  <span className="sl-v">
                                    {item.sr_admission_no}
                                  </span>
                                </div>
                                <div className="sl-info sl-info--inline">
                                  <span className="sl-k">Vill/Town:</span>
                                  <span className="sl-v">
                                    {item.village_town}
                                  </span>
                                </div>
                                <div className="sl-class-pill">
                                  Class: {item.class_name}
                                </div>
                              </div>
                            </div>

                            <div className="sl-aside-mini">
                              <div className="sl-roll-k">ROLL NO</div>
                              <div className="sl-roll">
                                {item.student_roll_no}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
